/**
 * A route chunk that no longer exists on the server.
 *
 * Every deploy renames the lazy route chunks (content hashes) and removes the
 * old ones. A tab opened before the deploy still holds the old names, so the
 * first navigation to a route it has not loaded yet fails with a ChunkLoadError,
 * and the error boundary showed "משהו השתבש" for what is only a stale page.
 * Three deploys in one morning made this the most common crash of the day.
 */
export function isChunkLoadError(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null | undefined;
  if (!e) return false;
  if (e.name === 'ChunkLoadError') return true;
  const message = String(e.message || '');
  return /Loading (CSS )?chunk [\w-]+ failed/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message);
}

const RELOAD_KEY = 'chunk-reload-at';
/** A second reload within this window means the server really is broken; stop looping. */
export const RELOAD_WINDOW_MS = 60_000;

/**
 * Whether this tab should reload itself now for a stale chunk: once, and not
 * again within RELOAD_WINDOW_MS, so a server that keeps failing shows the error
 * instead of a reload loop. Records the attempt when it answers true.
 */
export function shouldReloadForChunk(now: number = Date.now(), storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeSessionStorage()): boolean {
  if (!storage) return true;
  try {
    const last = Number(storage.getItem(RELOAD_KEY) || 0);
    if (last && now - last < RELOAD_WINDOW_MS) return false;
    storage.setItem(RELOAD_KEY, String(now));
    return true;
  } catch {
    return true;
  }
}

function safeSessionStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}
